import { generateContentId, resetIds } from './GenerateContentId.js';
import { UnlayerFonts } from './UnlayerFonts.js';

/**
 * The mapping of BARD block types to Unlayer column types.
 * The MAPS object contains two main properties: BLOCKTYPES and CONVERSIONS.
 * - BLOCKTYPES maps BARD types to corresponding Unlayer types, or to null if there's no direct mapping.
 * - CONVERSIONS maps BARD types to their conversion types for processing in corresponding conversion functions,
 *      and should therefore never be null.
 * @type {{BLOCKTYPES: Object<string, string|null>, CONVERSIONS: Object<string, string>}}
 * @example
 *  {
 *    BLOCKTYPES: {
 *      'BardType': 'UnlayerType' or null,
 *      // ... other mappings ...
 *    },
 *    CONVERSIONS: {
 *      'BardType': 'ConversionType',
 *      // ... other mappings ...
 *    }
 *  }
 */
const MAPS = {
  BLOCKTYPES: {
    text: null,
    bundle: null,
    button: 'button',
    divider: 'divider',
    image: 'image',
    spacer: 'divider',
    social: 'social',
    signature: 'custom_userSignature',
    video: 'video',
    html: 'html',
    'email-footer': 'custom_canSpamFooter',
    'emailFooter': 'custom_canSpamFooter'
  },
  CONVERSIONS: {
    text: 'text',
    bundle: 'bundle',
    button: 'button',
    divider: 'divider',
    image: 'image',
    spacer: 'divider',
    social: 'social',
    signature: 'signature',
    video: 'video',
    html: 'html',
    'email-footer': 'email-footer',
    'emailFooter': 'email-footer'
  }
};

/**
 * The counters for the various Unlayer components
 * @usage incrementCounters('u_row')
 * @type {Unlayer.Counters}
 */
export const counters = {};
let globalValues = {};
let migrationOptions = {};
export function setGlobalValues(values) {
  globalValues = values;
}
export const CONTACT_OWNER_SIGNATURE = '~Owner.EmailSignature~';
function incrementCounters(element) {
  if (!counters[element]) {
    counters[element] = 0;
  }
  counters[element]++;
}
export function resetCounters() {
  Object.keys(counters).forEach(key => {
    counters[key] = 0;
  });
}
function resetGlobals() {
  globalValues = {};
}
function resetMigration() {
  resetCounters();
  resetGlobals();
  resetIds();
  migrationOptions = {};
}

/**
 * Converts the BARD data to Unlayer data
 * @param bardJson {Bard.BardData} - The BARD data
 * @returns {Unlayer.UnlayerData} - The Unlayer data
 */
export function ConvertBardToUnlayer(bardJson, options = {}) {
  resetMigration();
  migrationOptions = options;
  return {
    counters,
    body: unlayerBody(convertBardV2_To_BardV1(bardJson)),
    schemaVersion: 14
  };
}

/**
 * Takes bard data and returns the Unlayer Body
 * @param bardJson {Bard.BardData} - The BARD data
 * @returns {Unlayer.UnlayerBody}
 */
export function unlayerBody({
  rows,
  'content-width': contentWidth = 600,
  globals
}) {
  globalValues = JSON.parse(JSON.stringify(globals));
  globalValues.contentWidth = contentWidth;
  return {
    id: generateContentId(),
    rows: [...rows.map(row => unlayerRow(row))],
    values: unlayerBodyValues({
      contentWidth: `${contentWidth}px`
    })
  };
}

/**
 * Creates the Unlayer body values from the BARD data
 * @param {string} contentWidth - The content width
 * @returns {Unlayer.BodyValues} - The Unlayer body values
 */
export function unlayerBodyValues({
  contentWidth
}) {
  const {
    alignment: contentAlign = 'left',
    'background-color': backgroundColor = '#FFFFFF',
    text: {
      a,
      p
    }
  } = globalValues;
  const {
    label,
    value
  } = getUnlayerFontFamily(p['font-family']);
  return {
    popupPosition: 'center',
    popupWidth: '600px',
    popupHeight: 'auto',
    borderRadius: '10px',
    contentAlign,
    contentVerticalAlign: 'center',
    contentWidth,
    fontFamily: {
      label,
      value
    },
    textColor: p.color,
    popupBackgroundColor: '#FFFFFF',
    popupBackgroundImage: {
      url: '',
      fullWidth: true,
      repeat: 'no-repeat',
      size: 'cover',
      position: 'center'
    },
    popupOverlay_backgroundColor: 'rgba(0, 0, 0, 0.1)',
    popupCloseButton_position: 'top-right',
    popupCloseButton_backgroundColor: '#DDDDDD',
    popupCloseButton_iconColor: '#000000',
    popupCloseButton_borderRadius: '0px',
    popupCloseButton_margin: '0px',
    popupCloseButton_action: {
      name: 'close_popup',
      attrs: {
        onClick: "document.querySelector('.u-popup-container').style.display = 'none';"
      }
    },
    backgroundColor,
    backgroundImage: {
      url: '',
      fullWidth: true,
      repeat: 'no-repeat',
      size: 'custom',
      position: 'top-center',
      customPosition: ['50%', '0%']
    },
    linkStyle: {
      body: true,
      linkColor: a.color,
      linkHoverColor: a.color,
      linkUnderline: true,
      linkHoverUnderline: true
    },
    _meta: {
      htmlID: 'u_body',
      htmlClassNames: 'u_body'
    }
  };
}

/**
 * Creates an Unlayer row from a BARD row
 * @param {Bard.Row} row - The row to create an Unlayer row from
 * @returns {Unlayer.Row} - The Unlayer row
 */
export function unlayerRow(row) {
  const {
    blocks
  } = row;
  incrementCounters('u_row');
  const rowIndex = counters.u_row;
  const columns = blocks.map(block => unlayerColFromBlock(block, blocks.length));
  return {
    id: generateContentId(),
    cells: new Array(columns.length).fill(1),
    columns,
    values: unlayerRowValues({
      rowIndex,
      columns
    })
  };
}

/**
 * Creates the Unlayer row values from a BARD row
 * @param rowIndex
 * @returns {Unlayer.RowValues}
 */
export function unlayerRowValues({
  rowIndex,
  columns
}) {
  var _columns$0$contents;
  let isLockedRow = false;
  if (columns.length === 1 && ((_columns$0$contents = columns[0].contents) === null || _columns$0$contents === void 0 ? void 0 : _columns$0$contents.length) === 1) {
    const {
      type,
      slug
    } = columns[0].contents[0];
    isLockedRow = type === 'custom' && slug === 'canSpamFooter';
  }
  return {
    displayCondition: null,
    columns: false,
    backgroundColor: '',
    columnsBackgroundColor: '#ffffff',
    backgroundImage: {
      url: '',
      fullWidth: true,
      repeat: 'no-repeat',
      size: 'custom',
      position: 'top-center',
      customPosition: ['50%', '0%']
    },
    padding: '0px',
    anchor: '',
    hideDesktop: false,
    _meta: {
      htmlID: `u_row_${rowIndex}`,
      htmlClassNames: 'u_row'
    },
    selectable: !isLockedRow,
    draggable: !isLockedRow,
    duplicatable: !isLockedRow,
    deletable: !isLockedRow,
    hideable: !isLockedRow,
    hideMobile: false,
    noStackMobile: false
  };
}

/**
 * Creates an Unlayer column from a BARD block
 * @param {Bard.Block} block
 * @param {number} numBlocks - Number of blocks in the column
 * @returns {Unlayer.Column}
 */
export function unlayerColFromBlock(block, numBlocksInColumn) {
  incrementCounters('u_column');
  const columnIndex = counters.u_column;
  const {
    blocks,
    columnPadding
  } = getUnlayerContentFromBlock(block, numBlocksInColumn);
  return {
    id: generateContentId(),
    contents: blocks,
    values: getUnlayerColValuesFromBlock(block, columnIndex, columnPadding)
  };
}

/**
 * Creates the Unlayer values from a BARD block
 * @param {Bard.Block} block
 * @param {number} rowIndex
 * @param {number} columnPadding
 * @returns {Unlayer.ColumnValues}
 */
export function getUnlayerColValuesFromBlock(block, rowIndex, columnPadding) {
  return {
    displayCondition: null,
    columns: false,
    backgroundColor: '',
    columnsBackgroundColor: '',
    backgroundImage: {
      url: '',
      fullWidth: true,
      repeat: false,
      center: true,
      cover: false
    },
    padding: columnPadding,
    hideDesktop: false,
    _meta: {
      htmlID: `u_column_${rowIndex}`,
      htmlClassNames: 'u_column'
    },
    selectable: true,
    draggable: true,
    duplicatable: true,
    deletable: true,
    hideable: true
  };
}

/**
 * Creates an Unlayer content block from a BARD block
 * @param {Bard.Block} block - The block to create an Unlayer content block from
 * @param numBlocksInColumn - Total number of blocks in the column the given block belongs to
 * @returns {{blocks: (Unlayer.Content[]), columnPadding: number}} - An object containing an array of Unlayer content
 *          blocks and the column padding for these blocks.
 */
export function getUnlayerContentFromBlock(block, numBlocksInColumn) {
  const blockType = block['block-type'];
  const type = getUnlayerTypeFromBardType(blockType);
  const conversion = getUnlayerConversionFromBardType(blockType);
  if (type) {
    const contentType = getContentType(type);
    incrementCounters(contentType);
  }
  let unlayerContent;
  let columnPadding = '0px';
  switch (conversion) {
    case 'text':
      unlayerContent = getUnlayerText(block);
      break;
    case 'bundle':
      ({
        blocks: unlayerContent,
        columnPadding
      } = getUnlayerBundle(block));
      break;
    case 'button':
      unlayerContent = getUnlayerButton(block);
      break;
    case 'divider':
      unlayerContent = getUnlayerDivider(block);
      break;
    case 'image':
      unlayerContent = getUnlayerImage(block, numBlocksInColumn);
      break;
    case 'social':
      unlayerContent = getUnlayerSocial(block);
      break;
    case 'email-footer':
      unlayerContent = getUnlayerEmailFooter(block);
      break;
    case 'video':
      unlayerContent = getUnlayerVideoBlock(block, numBlocksInColumn);
      break;
    case 'signature':
      unlayerContent = getUnlayerSignature(block);
      break;
    case 'html':
      unlayerContent = getUnlayerHTMLBlock(block);
      break;
    case undefined:
    default:
      unlayerContent = getUnlayerText({
        'block-type': 'text',
        text: {
          'block-padding': 15,
          content: `<p style='font-size: 14px; line-height: 130%;'><strong><span style='font-size: 48px; line-height: 62px;'>${type} NOT FULLY IMPLEMENTED</span></strong></p>`
        }
      });
      break;
  }
  const blocks = Array.isArray(unlayerContent) ? unlayerContent : [unlayerContent];
  return {
    blocks,
    columnPadding
  };
}

/**
 * Extracts social icons from a given social object.
 * @param {Object} social - Social links object from Bard.
 * @returns {Array} - Array of icon objects.
 */
function extractSocialIcons(social) {
  // Supported Social platforms
  const socialMediaPlatforms = [{
    key: 'facebook-url',
    name: 'Facebook'
  }, {
    key: 'twitter-url',
    name: 'Twitter'
  }, {
    key: 'linkedin-url',
    name: 'LinkedIn'
  }, {
    key: 'youtube-url',
    name: 'YouTube'
  }, {
    key: 'google-plus-url',
    name: 'GooglePlus'
  }, {
    key: 'pinterest-url',
    name: 'Pinterest'
  }, {
    key: 'instagram-url',
    name: 'Instagram'
  }];

  // Filter out any platforms that are not present, and map to the desired structure.
  return socialMediaPlatforms.filter(platform => typeof social[platform.key] === 'string').filter(platform => social[platform.key].trim() !== '').map(platform => {
    let url = social[platform.key].trim();
    const hasPrefix = url.indexOf('http://') === 0 || url.indexOf('https://') === 0;
    if (!hasPrefix) {
      url = `https://${url}`;
    }
    return {
      name: platform.name,
      url
    };
  });
}

/**
 * Converts Bard social block to Unlayer social block
 * @param {Bard.SocialBlock} block - Bard social block
 * @returns {Unlayer.SocialContent} - Unlayer social block's content
*/
export function getUnlayerSocial(block) {
  const {
    social
  } = block;
  const iconList = extractSocialIcons(social);
  return {
    id: generateContentId(),
    type: 'social',
    values: {
      containerPadding: `${social['block-padding']}px`,
      anchor: '',
      icons: {
        iconType: 'circle',
        icons: iconList,
        editor: {
          data: {
            showDefaultIcons: true,
            showDefaultOptions: true,
            customIcons: [],
            customOptions: []
          }
        }
      },
      _meta: getMetaValues(block['block-type']),
      align: social.alignment,
      spacing: social['icon-gap'],
      displayCondition: null,
      selectable: true,
      draggable: true,
      duplicatable: true,
      deletable: true,
      hideable: true
    }
  };
}

/**
 * Converts Bard signature block to Unlayer signature block
 * @param {Bard.SignatureBlock} block - Bard signature block
 * @returns {Unlayer.SignatureContent} - Unlayer signature block's content
*/
export function getUnlayerSignature(block) {
  let userId = '';
  let signature = block.signature.content;
  if (CONTACT_OWNER_SIGNATURE !== signature) {
    signature = signature.replaceAll('~', '');
    const [user] = signature.split('.');
    const [, uId] = user.split('_');
    userId = uId;
  }
  return {
    id: generateContentId(),
    type: 'custom',
    slug: 'userSignature',
    values: {
      displayCondition: null,
      containerPadding: '15px',
      anchor: '',
      _meta: getMetaValues(block['block-type']),
      selectable: true,
      draggable: true,
      duplicatable: true,
      deletable: true,
      hideable: true,
      userId
    }
  };
}

/**
 * Creates an Unlayer button block from a BARD block
 * @param {Bard.ButtonBlock} buttonBlock - The block to create an Unlayer button block from
 * @returns {Unlayer.ButtonContent} - The Unlayer button block
 */
export function getUnlayerButton(buttonBlock) {
  return {
    id: generateContentId(),
    type: 'button',
    values: getUnlayerButtonValues(buttonBlock)
  };
}

/**
 * Converts Bard video block to Unlayer video block
 * @param {Bard.VideoBlock} videoBlock - Bard video block
 * @param {number} numBlocksInColumn - Total number of blocks in the column the videoBlock belonged to
 * @returns {Unlayer.VideoContent} - Unlayer video block's content
 */
export function getUnlayerVideoBlock(videoBlock, numBlocksInColumn) {
  const {
    'block-padding': blockPadding = 15,
    'video-src': url = '',
    placeholder: thumbnail = '',
    alignment = 'center',
    'video-width-percent': videoWidthPercent = 100
  } = videoBlock.video;
  const blockType = 'video';
  const containerWidth = globalValues.contentWidth / numBlocksInColumn;
  const effectiveContainerWidth = containerWidth - blockPadding * 2;
  const bardVideoWidthPixels = effectiveContainerWidth * (videoWidthPercent / 100);
  const requiredUnlayerPadding = containerWidth - bardVideoWidthPixels;
  let unlayerPaddingLeft;
  let unlayerPaddingRight;
  if (alignment === 'left') {
    unlayerPaddingLeft = blockPadding;
    unlayerPaddingRight = Math.floor(requiredUnlayerPadding - blockPadding);
  } else if (alignment === 'right') {
    unlayerPaddingRight = blockPadding;
    unlayerPaddingLeft = Math.floor(requiredUnlayerPadding - blockPadding);
  } else {
    // center
    unlayerPaddingLeft = unlayerPaddingRight = Math.floor(requiredUnlayerPadding / 2);
  }
  const containerPadding = `${blockPadding}px ${unlayerPaddingRight}px ${blockPadding}px ${unlayerPaddingLeft}px`;
  return {
    id: generateContentId(),
    type: 'video',
    values: {
      containerPadding,
      video: {
        url,
        // contains merge link, should be replaced in post-processing
        thumbnail,
        videoId: '',
        type: ''
      },
      _meta: getMetaValues(blockType),
      selectable: true,
      draggable: true,
      duplicatable: true,
      deletable: true,
      hideable: true
    }
  };
}

/**
 * Converts Bard html block to Unlayer html block
 * @param {Bard.HtmlBlock} htmlBlock - Bard html block
 * @returns {Unlayer.HTMLContent} - Unlayer html block's content
 */
export function getUnlayerHTMLBlock(htmlBlock) {
  const {
    html = ''
  } = htmlBlock.html || {};
  const {
    applyStylesToHtml = true
  } = migrationOptions;
  let parsedHtml;
  let containerPadding;
  const adjustMargin = el => {
    const marginTop = el.style['margin-top'];
    const marginBottom = el.style['margin-bottom'];
    const marginLeft = el.style['margin-left'];
    const marginRight = el.style['margin-right'];
    const margin = el.style.margin;
    if (marginTop || marginBottom || marginLeft || marginRight) {
      el.style['margin-top'] = marginTop || 'revert';
      el.style['margin-bottom'] = marginBottom || 'revert';
      el.style['margin-left'] = marginLeft || 'revert';
      el.style['margin-right'] = marginRight || 'revert';
    } else if (!margin) {
      el.style.margin = 'revert';
    }
  };

  // these css rules are hardcoded in bard
  // they are not applied in the builder but they are set in the email.
  if (applyStylesToHtml) {
    const parser = new DOMParser().parseFromString(`<html><head></head><body><div>\n${html}\n</div></body></html>`, 'text/html');
    const container = parser.querySelector('body > div');
    container.style.setProperty('font-family', 'Helvetica, Arial, sans-serif');
    container.style.setProperty('font-size', '20px');
    container.style.setProperty('text-align', 'center');
    container.querySelectorAll('p').forEach(adjustMargin);
    parsedHtml = container.outerHTML;
    containerPadding = '0px';
  } else {
    const parser = new DOMParser().parseFromString(html, 'text/html');
    parser.querySelectorAll('p').forEach(adjustMargin);
    parsedHtml = parser.body.innerHTML;
    containerPadding = '10px';
  }
  return {
    id: generateContentId(),
    type: 'html',
    values: {
      html: parsedHtml.trim(),
      containerPadding,
      _meta: getMetaValues(htmlBlock['block-type']),
      selectable: true,
      draggable: true,
      duplicatable: true,
      deletable: true,
      hideable: true
    }
  };
}

/**
 * Creates the Unlayer button values from a BARD button
 * @param {Bard.ButtonBlock} buttonBlock - The button to create Unlayer button values from
 * @returns {Unlayer.ButtonValues}
 */
export function getUnlayerButtonValues(buttonBlock) {
  const {
    alignment: textAlign,
    'background-color': backgroundColor,
    'block-padding': containerPadding,
    color,
    'font-family': fontFamily,
    'font-size': fontSize,
    padding,
    text,
    url = ''
  } = buttonBlock.button;
  return {
    containerPadding: `${containerPadding}px`,
    textAlign,
    anchor: '',
    href: getUnlayerHref(url),
    buttonColors: getUnlayerButtonColors(backgroundColor, color),
    size: {
      autoWidth: true,
      width: '100%'
    },
    fontFamily: getUnlayerFontFamily(fontFamily),
    fontWeight: 700,
    fontSize: `${fontSize}px`,
    lineHeight: '150%',
    // hardcoded to this value in bard
    padding: `${padding}px`,
    text,
    border: {},
    borderRadius: '4px',
    _meta: getMetaValues(buttonBlock['block-type']),
    hideDesktop: false,
    displayCondition: null,
    selectable: true,
    draggable: true,
    duplicatable: true,
    deletable: true,
    hideable: true
  };
}

/**
 *
 * @param {string} backgroundColor
 * @param {string} color
 * @returns {Unlayer.ButtonColors}
 */
export function getUnlayerButtonColors(backgroundColor, color) {
  return {
    color,
    backgroundColor,
    hoverColor: color,
    hoverBackgroundColor: backgroundColor
  };
}

/**
 * Checks if the Unlayer font matches the supported font
 * @param {string} supportedFont - Supported font string to compare
 * @returns {function(Unlayer.FontFamily): boolean} Function that compares an Unlayer font to the supported font
 */
function isMatchingUnlayerFont(supportedFont) {
  return unlayerFont => supportedFont === (unlayerFont && unlayerFont.label ? unlayerFont.label.toLowerCase() : '');
}

/**
 * Gets the supported Unlayer font family given a Bard font family
 * @param {string} bardFontFamily - Bard font family string
 * @returns {Unlayer.FontFamily} Object with label and value of the Unlayer supported font family
 */
export function getUnlayerFontFromBardFontFamily(bardFontFamily) {
  // Split Bard font families, trim whitespace, convert to lowercase, and add 'arial' as a fallback
  const bardFontFamilies = bardFontFamily && typeof bardFontFamily === 'string' ? bardFontFamily.toLowerCase().split(',').map(font => font.replaceAll('"', '').replaceAll('\'', '').trim()) : [];
  bardFontFamilies.push('arial'); // Ensure there is a fallback

  const supportedUnlayerFont = bardFontFamilies.find(font => UnlayerFonts.find(isMatchingUnlayerFont(font)));
  return UnlayerFonts.find(isMatchingUnlayerFont(supportedUnlayerFont));
}

/**
 * gets formatted unlayer font family values
 * @param {string} fontFamily - A comma separated list of font families
 * @returns {Unlayer.FontFamily}
 */
export function getUnlayerFontFamily(fontFamily) {
  return {
    ...getUnlayerFontFromBardFontFamily(fontFamily),
    defaultFont: true // indicates if this is a default font or custom font in unlayer editor. https://docs.unlayer.com/docs/font-management#adding-custom-fonts
  };
}

/**
 * returns formatted unlayer button href
 * @param url
 * @returns {Unlayer.Href}
 */
export function getUnlayerHref(url) {
  return {
    name: 'web',
    attrs: {
      href: '{{href}}',
      target: '_blank',
      'data-tag-id': '{{tag}}'
    },
    values: {
      href: url,
      target: '_blank',
      tag: []
    }
  };
}

/**
 * Creates an email footer block from Unlayer
 * @param {Bard.EmailFooterBlock} block - The block to create an email footer
 * @returns {Unlayer.EmailFooterContent} - The Unlayer email footer
 */
export function getUnlayerEmailFooter({
  'email-footer': emailFooter,
  'block-type': blockType
}) {
  var _emailFooter$alignmen;
  const {
    'additional-text': additionalText,
    'unsubscribe-pre-text': linkPreText,
    'unsubscribe-post-text': linkPostText
  } = emailFooter;
  return {
    id: generateContentId(),
    type: 'custom',
    slug: 'canSpamFooter',
    values: {
      containerPadding: '10px',
      alignment: (_emailFooter$alignmen = emailFooter === null || emailFooter === void 0 ? void 0 : emailFooter.alignment) !== null && _emailFooter$alignmen !== void 0 ? _emailFooter$alignmen : 'left',
      additionalText,
      linkPreText,
      linkPostText,
      _meta: getMetaValues(blockType),
      selectable: true,
      draggable: false,
      duplicatable: false,
      deletable: false,
      hideable: false
    }
  };
}

/**
 * Creates a divider block from Unlayer
 * @param {Bard.DividerBlock|Bard.SpacerBlock} block - The block to create an Unlayer divider from
 * @returns {Unlayer.DividerContent} - The Unlayer divider block
 */
export function getUnlayerDivider(block) {
  var _block$divider$width, _block$divider, _block$divider$block, _block$divider2, _block$divider$color, _block$divider3;
  const asSpacer = block['block-type'] === 'spacer';
  let width = `${(_block$divider$width = block === null || block === void 0 ? void 0 : (_block$divider = block.divider) === null || _block$divider === void 0 ? void 0 : _block$divider['width-percent']) !== null && _block$divider$width !== void 0 ? _block$divider$width : 100}%`;
  let height = (_block$divider$block = block === null || block === void 0 ? void 0 : (_block$divider2 = block.divider) === null || _block$divider2 === void 0 ? void 0 : _block$divider2['block-padding']) !== null && _block$divider$block !== void 0 ? _block$divider$block : 15;
  const color = (_block$divider$color = block === null || block === void 0 ? void 0 : (_block$divider3 = block.divider) === null || _block$divider3 === void 0 ? void 0 : _block$divider3.color) !== null && _block$divider$color !== void 0 ? _block$divider$color : '#BBBBBB';
  let containerPadding = `${height}px`;
  if (asSpacer) {
    var _block$spacer$height, _block$spacer;
    const containerPaddingTop = (_block$spacer$height = block === null || block === void 0 ? void 0 : (_block$spacer = block.spacer) === null || _block$spacer === void 0 ? void 0 : _block$spacer.height) !== null && _block$spacer$height !== void 0 ? _block$spacer$height : height;
    width = '0%';
    containerPadding = `${containerPaddingTop}px 0px 0px 0px`;
  }
  return {
    id: generateContentId(),
    type: 'divider',
    values: {
      width,
      border: {
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderTopColor: color
      },
      containerPadding,
      _meta: getMetaValues(block['block-type']),
      displayCondition: null,
      selectable: true,
      draggable: true,
      duplicatable: true,
      deletable: true,
      hideable: true
    }
  };
}

/**
 * Converts a dirty line height value to a percentage string.
 * @param {string|number} dirtyLineHeight - The raw line height value.
 * @returns {string} The line height as a percentage string.
 */
function formatLineHeight(dirtyLineHeight) {
  const lineHeightNumber = Number.parseFloat(dirtyLineHeight);
  const effectiveLineHeight = isNaN(lineHeightNumber) ? 1.4 : lineHeightNumber;
  return `${effectiveLineHeight * 100}%`;
}

/**
 * Gets Unlayer text from a BARD block
 * @param {Bard.TextBlock} textBlock - The block to get the text from
 * @returns {Array<Unlayer.TextContent|Unlayer.HeadingContent>} Array of Unlayer blocks. Block can be a text block or a heading block.
 */
export function getUnlayerText(textBlock) {
  const {
    'block-padding': blockPadding,
    content
  } = textBlock.text;
  const {
    color: linkColor
  } = globalValues.text.a;
  const {
    'font-size': fontSize,
    'line-height': dirtyLineHeight
  } = globalValues.text.p;
  const lineHeight = formatLineHeight(dirtyLineHeight);
  const formattedBlockPadding = Number(blockPadding) === blockPadding ? blockPadding : 15;
  function getMargin(nodeName) {
    // Bard sets a margin top and bottom for text elements
    // These are not present in the design json
    // values are in px
    // for p, u, b, i css margin is set to `13px 0`
    // for h1, ol and ul it's set to `16px 0`
    // for h2 it's set to `15px 0`
    let marginTopAndBottom;
    switch (nodeName.toLowerCase()) {
      case 'h1':
      case 'ol':
      case 'ul':
        marginTopAndBottom = 16;
        break;
      case 'h2':
        marginTopAndBottom = 15;
        break;
      default:
        marginTopAndBottom = 13;
        break;
    }
    return {
      top: marginTopAndBottom,
      bottom: marginTopAndBottom,
      left: 0,
      right: 0,
      toString: function () {
        return this.top === 0 && this.bottom === 0 ? null : `${this.top}px ${this.right}px ${this.bottom}px ${this.left}px`;
      }
    };
  }
  function getContainerPadding() {
    return {
      top: formattedBlockPadding,
      bottom: formattedBlockPadding,
      left: formattedBlockPadding,
      right: formattedBlockPadding,
      toString: function () {
        return `${this.top}px ${this.right}px ${this.bottom}px ${this.left}px`;
      }
    };
  }
  const dom = new DOMParser().parseFromString(content, 'text/html');
  const elements = dom.querySelectorAll('body > *');
  elements.forEach(el => el.removeAttribute('class'));
  const groups = [{
    elements: [],
    containerPadding: getContainerPadding()
  }];
  const pushElementToNewGroup = el => groups.push({
    elements: [{
      el,
      margin: getMargin(el.nodeName)
    }],
    containerPadding: getContainerPadding()
  });
  const pushElementToGroup = (group, el) => group.elements.push({
    el,
    margin: getMargin(el.nodeName)
  });

  /*
  Split elements into groups. Each group will become an unlayer block.
  h1 or h2 are put in their own group
  other elements are grouped together if text alignment is the same
  The rest are put into same group if possible
  Example:
      <p/>
      <b/>
      <p/>
      <h1/>
      <h2/>
      <u/>
      <b/>
      <h1/>
      <p/>
      <h2/>
      <p text-align="center"/>
      <p text-align="center"/>
      <p text-align="right"/>
       The above elements will be grouped like this:
      [
          [<p/>, <b/>, <p/>],
          [<h1/>],
          [<h2/>],
          [<u/>, <b/>],
          [<h1/>],
          [<p/>],
          [<h2/>],
          [<p text-align="center"/>, <p text-align="center"/>],
          [<p text-align="right"/>]
      ]
  */
  let previousSibling = '';
  for (const el of elements) {
    const nodeName = el.nodeName.toLowerCase();
    const existingGroup = groups[groups.length - 1];
    const textAlign = el.style.textAlign;
    if (nodeName === 'h1' || nodeName === 'h2' || textAlign !== previousSibling) {
      if (existingGroup.elements.length === 0) {
        pushElementToGroup(existingGroup, el);
      } else {
        pushElementToNewGroup(el);
      }
    } else {
      if (existingGroup.elements.length === 0) {
        pushElementToGroup(existingGroup, el);
      } else {
        const mostRecentEl = existingGroup.elements[existingGroup.elements.length - 1];
        const nodeName = mostRecentEl.el.nodeName.toLowerCase();
        if (nodeName === 'h1' || nodeName === 'h2') {
          pushElementToNewGroup(el);
        } else {
          pushElementToGroup(existingGroup, el);
        }
      }
    }
    previousSibling = textAlign;
  }

  // *Execution order matters for the following rule sets*
  // Rule set 1
  // 1. First element of first group should not have margin-top
  // 2. Last element of last group should not have margin-bottom
  (() => {
    const firstGroup = groups[0];
    const lastGroup = groups[groups.length - 1];
    const firstElement = firstGroup.elements[0];
    const lastElement = lastGroup.elements[lastGroup.elements.length - 1];
    firstElement.margin.top = 0;
    lastElement.margin.bottom = 0;
  })();
  // end rule set 1

  // Rule set 2
  // 1. Remove container padding top if group is not the first group
  // 2. Remove container padding bottom if the group is not the last group
  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];
    if (i !== 0) {
      group.containerPadding.top = 0;
    }
    if (i !== groups.length - 1) {
      group.containerPadding.bottom = 0;
    }
  }
  // end rule set 2

  /* Rule set 3
   1. Make sure there is no margin between groups.
      We are doing this because unlayer has no option to set margin for container/block.
      If we set margin on html element it will work in unlayer too but user will not
      be able to change it.
       Assume there are two groups (g1 and g2). g2 is below g1.
      Convert margin-bottom of last element of g1 into container padding bottom of g1
      And convert margin-top of first element of g2 into container padding top of g2
      (Important) Take css margin-collapse into consideration.
  */
  for (let i = 0; i < groups.length - 1; i++) {
    const g1 = groups[i];
    const g2 = groups[i + 1];
    const p1 = g1.containerPadding;
    const p2 = g2.containerPadding;
    const lastElementOfG1 = g1.elements[g1.elements.length - 1];
    const firstElementOfG2 = g2.elements[0];
    const marginBottom = lastElementOfG1.margin.bottom;
    const marginTop = firstElementOfG2.margin.top;

    // Assuming there is 0 padding between the elements, according to css margin-collapse
    // *in this particular scenario* the distance between elements will be equal to the greater margin of the two margins
    // Since padding will not collapse like margin, we can split the greatestMargin, as padding between the two containers.
    const greatestMargin = marginTop > marginBottom ? marginTop : marginBottom;
    const isMarginOddNumber = greatestMargin % 2 !== 0;
    const padding = Number.parseInt(`${greatestMargin / 2}`);
    p1.bottom += padding;
    p2.top += padding;
    if (isMarginOddNumber) {
      p2.top += 1;
    }
    lastElementOfG1.margin.bottom = 0;
    firstElementOfG2.margin.top = 0;
  }
  // end rule set 3

  function getTextBlock(text, containerPadding, textAlign) {
    const contentType = 'u_content_text';
    incrementCounters(contentType);
    const htmlID = `${contentType}_${counters[contentType]}`;
    return {
      id: generateContentId(),
      type: 'text',
      values: {
        containerPadding: containerPadding.toString(),
        text,
        fontSize: fontSize,
        textAlign,
        lineHeight,
        linkStyle: {
          inherit: true,
          linkColor,
          linkHoverColor: linkColor,
          linkUnderline: true,
          linkHoverUnderline: true
        },
        _meta: {
          htmlID,
          htmlClassNames: contentType
        },
        fontFamily: {
          label: 'Global Font',
          value: 'inherit'
        },
        selectable: true,
        draggable: true,
        duplicatable: true,
        deletable: true,
        hideable: true
      }
    };
  }
  function getHeading(headingType, content, containerPadding, textAlign = 'left', color = null) {
    const contentType = 'u_content_heading';
    incrementCounters(contentType);
    const htmlID = `${contentType}_${counters[contentType]}`;
    return {
      id: generateContentId(),
      type: 'heading',
      values: {
        containerPadding: containerPadding.toString(),
        anchor: '',
        headingType,
        fontFamily: getUnlayerFontFamily(globalValues.text[headingType]['font-family']),
        fontSize: globalValues.text[headingType]['font-size'],
        fontWeight: 700,
        color: color ? color : globalValues.text[headingType].color,
        textAlign,
        lineHeight: formatLineHeight(globalValues.text[headingType]['line-height']),
        linkStyle: {
          inherit: true,
          linkColor: '#0000ee',
          linkHoverColor: '#0000ee',
          linkUnderline: true,
          linkHoverUnderline: true
        },
        displayCondition: null,
        _meta: {
          htmlID,
          htmlClassNames: contentType
        },
        selectable: true,
        draggable: true,
        duplicatable: true,
        deletable: true,
        hideable: true,
        text: content
      }
    };
  }
  const rgbColorRegex = /^rgb[a]?[(]\s*\d+\s*[,]\s*\d+\s*[,]\s*\d+\s*([,]*\s*(\d+(\.\d+){0,1})\s*)*[)][;]*$/;
  const hexColorRegex = /^#([a-f0-9]{6}|[a-f0-9]{3});*$/;

  /**
   * Converts an RGB color to a hex color.
   * @param {string} rgb - The RGB color to convert in the format 'rgb(255, 255, 255)', where each number
   *      is between 0 and 255. the string may optionally end with a semicolon.
   * @returns {string} The hex color in the format '#rrggbb'.
   */
  function rgbToHex(rgb) {
    const componentToHex = c => {
      const hex = parseInt(c, 10).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    const [r, g, b] = rgb.match(/\d+/g).map(Number).map(componentToHex);
    return `#${r}${g}${b}`;
  }
  function getColor(color) {
    color = color.toLowerCase().trim();
    if (rgbColorRegex.test(color)) {
      return rgbToHex(color);
    }

    // colors defined in inline style attrbute only seem to be defined as rgb functions
    // this check only exists in case some older template stored color as a hex
    if (hexColorRegex.test(color)) {
      const hexColor = color.replaceAll(';', '');

      // if there are only 3 hex chars convert them to 6 hex chars format.
      if (hexColor.length === 4) {
        // 4 chars including #
        const formattedHex = hexColor.replace('#', '').split('').map(hexVal => `${hexVal}${hexVal}`).join('');
        return `#${formattedHex}`;
      }
      return hexColor;
    }
    return null;
  }
  const unlayerBlocks = [];
  for (let i = 0; i < groups.length; i++) {
    const {
      elements,
      containerPadding
    } = groups[i];

    // since elements are also grouped by textAlign we can
    // get the textAlign for the first element to check
    // what the text align is going to be for all elements in the group
    const textAlign = elements[0].el.style.textAlign.toLowerCase();
    const parsedTextAlign = textAlign === 'center' || textAlign === 'right' || textAlign === 'justify' ? textAlign : 'left';
    if (elements.length === 1) {
      const {
        el,
        margin
      } = elements[0];
      const nodeName = el.nodeName.toLowerCase();
      if (nodeName === 'h1' || nodeName === 'h2') {
        const parsedColor = getColor(el.style.color);
        unlayerBlocks.push(getHeading(nodeName, el.innerHTML, containerPadding, parsedTextAlign, parsedColor));
      } else {
        if (margin.toString()) {
          el.style.setProperty('margin', margin.toString());
        }
        el.style.textAlign = '';
        unlayerBlocks.push(getTextBlock(el.outerHTML, containerPadding, parsedTextAlign));
      }
    } else {
      const container = document.createElement('div');
      for (let j = 0; j < elements.length; j++) {
        const {
          el,
          margin
        } = elements[j];
        if (margin.toString()) {
          el.style.setProperty('margin', margin.toString());
        }
        el.style.textAlign = '';
        container.insertAdjacentHTML('beforeEnd', el.outerHTML);
      }
      unlayerBlocks.push(getTextBlock(container.innerHTML, containerPadding, parsedTextAlign));
    }
  }
  return unlayerBlocks;
}

/**
 * Returns something like a BARD Bundle but compatible with Unlayer
 * @param {Bard.BundleBlock} block
 * @returns {Unlayer.BundleContent}
 */
export function getUnlayerBundle(block) {
  const bundleBlock = [];
  const {
    'block-padding': blockPadding = 15,
    button,
    image,
    layout,
    text
  } = block.bundle;
  const columnPadding = blockPadding;
  const showImage = layout !== 'hide-image';
  const showText = layout !== 'hide-text';
  const showButton = layout !== 'hide-button';
  const getSpacer = () => {
    // There is a spacer of 15px between blocks in bard.
    const height = 15;
    incrementCounters('u_content_divider');
    return getUnlayerDivider({
      'block-type': 'spacer',
      spacer: {
        height
      }
    }, true);
  };
  if (showImage) {
    const imageBlock = {
      'block-type': 'image',
      image
    };
    incrementCounters('u_content_image');
    bundleBlock.push(getUnlayerImage(imageBlock, 1));
  }
  if (showText) {
    if (showImage) {
      bundleBlock.push(getSpacer());
    }
    const textBlock = {
      'block-type': 'text',
      text
    };
    bundleBlock.push(...getUnlayerText(textBlock));
  }
  if (showButton) {
    bundleBlock.push(getSpacer());
    const buttonBlock = {
      'block-type': 'button',
      button
    };
    incrementCounters('u_content_button');
    bundleBlock.push(getUnlayerButton(buttonBlock));
  }
  return {
    blocks: bundleBlock,
    columnPadding: `${columnPadding}px`
  };
}

/**
 * Gets Unlayer image from a Bard block
 * @param {Bard.ImageBlock} imageBlock
 * @param {number} numBlocksInColumn - Total number of blocks in the column the bard image block is a part of
 * @returns {Unlayer.ImageContent} - The Unlayer image content
 */
export function getUnlayerImage(imageBlock, numBlocksInColumn) {
  return {
    id: generateContentId(),
    type: 'image',
    values: getUnlayerImageValues(imageBlock, numBlocksInColumn)
  };
}

/**
 * Gets Unlayer image values from a Bard image
 * @param {Bard.ImageBlock} imageBlock
 * @param {number} numBlocksInColumn - Total number of blocks in the column the bard image block is a part of
 * @returns {Unlayer.ImageValues}
 */
export function getUnlayerImageValues(imageBlock, numBlocksInColumn) {
  const {
    alignment,
    alt,
    'image-width-percent': imageWidthPercent,
    src = '',
    url = ''
  } = imageBlock.image;
  const containerPadding = numBlocksInColumn > 1 ? '15px 0 15px 0' : '0px';
  return {
    containerPadding,
    textAlign: alignment,
    altText: alt || '',
    action: {
      name: 'web',
      attrs: {
        href: '{{href}}',
        target: '_blank',
        'data-tag-id': '{{tag}}'
      },
      values: {
        href: url,
        tag: [],
        target: '_blank'
      }
    },
    src: {
      url: src,
      autoWidth: false,
      maxWidth: `${imageWidthPercent}%`,
      height: 'auto'
    },
    _meta: getMetaValues(imageBlock['block-type']),
    hideDesktop: false,
    hideMobile: false,
    displayCondition: null,
    selectable: true,
    draggable: true,
    duplicatable: true,
    deletable: true,
    hideable: true,
    pending: false
  };
}

/**
 * Gets the Unlayer type from a Bard type
 * @param {string} bardType
 * @returns {string}
 */
export function getUnlayerTypeFromBardType(bardType) {
  var _MAPS$BLOCKTYPES$bard;
  return (_MAPS$BLOCKTYPES$bard = MAPS.BLOCKTYPES[bardType]) !== null && _MAPS$BLOCKTYPES$bard !== void 0 ? _MAPS$BLOCKTYPES$bard : null;
}

/**
 * Gets the Unlayer conversion function type from a Bard type
 * @param {string} bardType
 * @returns {string}
 */
export function getUnlayerConversionFromBardType(bardType) {
  var _MAPS$CONVERSIONS$bar;
  return (_MAPS$CONVERSIONS$bar = MAPS.CONVERSIONS[bardType]) !== null && _MAPS$CONVERSIONS$bar !== void 0 ? _MAPS$CONVERSIONS$bar : null;
}

/**
 * returns the unlayer content type in the form of u_content_{type}
 * @param {string} type
 * @returns {string}
 */
export function getContentType(type) {
  return `u_content_${type}`;
}
export function getMetaValues(blockType) {
  const type = getUnlayerTypeFromBardType(blockType);
  const contentType = getContentType(type);
  return {
    htmlID: `${contentType}_${counters[contentType]}`,
    htmlClassNames: contentType
  };
}
export function camelToKebab(obj) {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(camelToKebab);
  }
  return Object.keys(obj).reduce((acc, key) => {
    const kebabKey = key.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
    acc[kebabKey] = camelToKebab(obj[key]);
    return acc;
  }, {});
}
function convertBardV2_To_BardV1(jsonData) {
  if (jsonData.version === '2.1.0') {
    if (jsonData.rows) {
      jsonData.rows.forEach(row => {
        if (row.blocks) {
          row.blocks.forEach(block => {
            if (block.blockType === 'text' && !block.content) {
              block.text.content = '<p class="style-scope bard-text-block"></p>';
            }
          });
        }
      });
    }
    jsonData = camelToKebab(jsonData);
  }
  return jsonData;
}